import React, { useEffect, useMemo, useState } from "react";
import jwtDecode from "jwt-decode";
import Translator from "./translator";
import { formatMontant } from "../utils/formatting.js";
import { getFacturesDuesHistoriqueByIdProfilePav, getFacturesHistoriqueByIdProfilePav } from "../server/services/client-service.js";
import "../../css/profile-historique-achats-tab.css";

function normalizeFactureRow(raw) {
  const noFacture = raw.noFacture ?? raw.no_facture ?? raw.NoFacture ?? "—";
  const dateRaw = raw.dateDeVente ?? raw.date_de_vente ?? raw.DateDeVente ?? null;
  const montant = Number(raw.montantFacture ?? raw.montant_facture ?? raw.MontantFacture ?? 0);
  const solde = Number(raw.solde ?? raw.Solde ?? 0);

  return {
    id: raw.idFacturationEntete ?? raw.id_facturation_entete ?? raw.IdFacturationEntete ?? noFacture + String(dateRaw),
    noFacture: String(noFacture),
    dateRaw,
    montant: Number.isFinite(montant) ? montant : 0,
    solde: Number.isFinite(solde) ? solde : 0,
  };
}

function isFacturePayee(solde) {
  return Math.abs(Number(solde)) < 0.005;
}

function formatFactureDate(dateRaw, language) {
  if (dateRaw == null || dateRaw === "")
    return "—";

  try {
    const d = new Date(dateRaw);
    if (Number.isNaN(d.getTime()))
      return "—";

    return d.toLocaleDateString(language === "en" ? "en-CA" : "fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

const SORT_NO_FACTURE = "noFacture";
const SORT_DATE = "date";
const SORT_MONTANT = "montant";

function compareLocale(a, b, language) {
  return String(a).localeCompare(String(b), language === "en" ? "en-CA" : "fr-CA", { sensitivity: "base", numeric: true });
}

function sortFacturesRows(rows, sortKey, sortDir, language) {
  const mult = sortDir === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    if (sortKey === SORT_NO_FACTURE)
      return mult * compareLocale(a.noFacture, b.noFacture, language);

    if (sortKey === SORT_DATE) {
      const ta = a.dateRaw ? new Date(a.dateRaw).getTime() : 0;
      const tb = b.dateRaw ? new Date(b.dateRaw).getTime() : 0;

      return mult * (ta - tb);
    }

    if (sortKey === SORT_MONTANT)
      return mult * (a.montant - b.montant);

    return 0;
  });
}

function defaultDirForSortKey(sortKey) {
  if (sortKey === SORT_NO_FACTURE)
    return "asc";

  return "desc";
}

export default function ProfileHistoriqueAchatsTab({ language = "fr", idZec, authToken, setAlert }) {
  const TEXTS = useMemo(() => Translator(language, "TXT_PROFILE_PAGE"), [language]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [factures, setFactures] = useState([]);
  const [sortKey, setSortKey] = useState(SORT_DATE);
  const [sortDir, setSortDir] = useState("desc");

  const facturesTriees = useMemo(
    () => sortFacturesRows(factures, sortKey, sortDir, language),
    [factures, sortKey, sortDir, language],
  );

  const onTriColonne = (key) => {
    if (key === sortKey)
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(defaultDirForSortKey(key));
    }
  };

  useEffect(() => {
    if (!authToken || !idZec) {
      setLoading(false);
      setFactures([]);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    let decoded;
    try {
      decoded = jwtDecode(authToken);
    } catch {
      setLoading(false);
      setError(TEXTS.historiqueAchatsErreur);
      return;
    }

    const idProfile = decoded.idProfile;

    Promise.all([
      getFacturesHistoriqueByIdProfilePav(idProfile),
      getFacturesDuesHistoriqueByIdProfilePav(idProfile),
    ])
      .then(([facturesHistorique, facturesDuesHistorique]) => {
        if (cancelled)
          return;

        const hist = facturesHistorique === null ? [] : facturesHistorique;
        const dues = facturesDuesHistorique === null ? [] : facturesDuesHistorique;

        if (!Array.isArray(hist) || !Array.isArray(dues)) {
          setError(TEXTS.historiqueAchatsErreur);
          setFactures([]);
          if (typeof setAlert === "function")
            setAlert(TEXTS.historiqueAchatsErreur, "danger");
          return;
        }

        setError(null);

        const mapById = new Map();

        hist.map(normalizeFactureRow).forEach((row) => {
          mapById.set(row.id, row);
        });
        dues.map(normalizeFactureRow).forEach((row) => {
          mapById.set(row.id, row);
        });

        const rows = Array.from(mapById.values());

        setFactures(rows);
      })
      .catch(() => {
        if (cancelled)
          return;

        setError(TEXTS.historiqueAchatsErreur);
        setFactures([]);
        if (typeof setAlert === "function")
          setAlert(TEXTS.historiqueAchatsErreur, "danger");
      })
      .finally(() => {
        if (!cancelled)
          setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, idZec, language, setAlert, TEXTS]);

  return (
    <div className="profile-historique-achats-tab" data-language={language || ""}>
      <h2 className="profile-historique-achats-title">
        <span className="profile-span">{TEXTS.historiqueAchatsListeTitre}</span>
      </h2>

      {loading && (
        <p className="profile-historique-achats-message profile-historique-achats-message--muted">
          {TEXTS.historiqueAchatsChargement}
        </p>
      )}

      {!loading && error && !factures.length && (
        <p className="profile-historique-achats-message profile-historique-achats-message--error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && factures.length === 0 && (
        <p className="profile-historique-achats-message profile-historique-achats-message--muted">
          {TEXTS.historiqueAchatsVide}
        </p>
      )}

      {!loading && factures.length > 0 && (
        <div className="profile-historique-achats-table-wrap">
          <table className="profile-historique-achats-table">
            <thead>
              <tr>
                <th
                  scope="col"
                  aria-sort={sortKey === SORT_NO_FACTURE ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <button
                    type="button"
                    className={
                      "profile-historique-achats-sort" +
                      (sortKey === SORT_NO_FACTURE ? " profile-historique-achats-sort--active" : "")
                    }
                    onClick={() => onTriColonne(SORT_NO_FACTURE)}
                    title={TEXTS.historiqueAchatsTriFacture}
                    aria-label={TEXTS.historiqueAchatsTriFacture}
                  >
                    <span>{TEXTS.historiqueAchatsColFacture}</span>
                    <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                      <span
                        className={
                          "profile-historique-achats-sort-icon-char" +
                          (sortKey === SORT_NO_FACTURE ? "" : " profile-historique-achats-sort-icon-char--reserve")
                        }
                      >
                        {sortKey === SORT_NO_FACTURE ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                      </span>
                    </span>
                  </button>
                </th>
                <th
                  scope="col"
                  aria-sort={sortKey === SORT_DATE ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <button
                    type="button"
                    className={
                      "profile-historique-achats-sort" +
                      (sortKey === SORT_DATE ? " profile-historique-achats-sort--active" : "")
                    }
                    onClick={() => onTriColonne(SORT_DATE)}
                    title={TEXTS.historiqueAchatsTriDate}
                    aria-label={TEXTS.historiqueAchatsTriDate}
                  >
                    <span>{TEXTS.historiqueAchatsColDate}</span>
                    <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                      <span
                        className={
                          "profile-historique-achats-sort-icon-char" +
                          (sortKey === SORT_DATE ? "" : " profile-historique-achats-sort-icon-char--reserve")
                        }
                      >
                        {sortKey === SORT_DATE ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                      </span>
                    </span>
                  </button>
                </th>
                <th
                  scope="col"
                  className="profile-historique-achats-th-numeric"
                  aria-sort={sortKey === SORT_MONTANT ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                >
                  <button
                    type="button"
                    className={
                      "profile-historique-achats-sort profile-historique-achats-sort--numeric" +
                      (sortKey === SORT_MONTANT ? " profile-historique-achats-sort--active" : "")
                    }
                    onClick={() => onTriColonne(SORT_MONTANT)}
                    title={TEXTS.historiqueAchatsTriMontant}
                    aria-label={TEXTS.historiqueAchatsTriMontant}
                  >
                    <span>{TEXTS.historiqueAchatsColMontant}</span>
                    <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                      <span
                        className={
                          "profile-historique-achats-sort-icon-char" +
                          (sortKey === SORT_MONTANT ? "" : " profile-historique-achats-sort-icon-char--reserve")
                        }
                      >
                        {sortKey === SORT_MONTANT ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                      </span>
                    </span>
                  </button>
                </th>
                <th scope="col">{TEXTS.historiqueAchatsColPaiement}</th>
              </tr>
            </thead>
            <tbody>
              {facturesTriees.map((row) => {
                const paye = isFacturePayee(row.solde);

                return (
                  <tr key={row.id}>
                    <td className="profile-historique-achats-cell-no">{row.noFacture}</td>
                    <td>{formatFactureDate(row.dateRaw, language)}</td>
                    <td className="profile-historique-achats-cell-numeric">{formatMontant(row.montant, language)}</td>
                    <td>
                      <span
                        className={
                          "profile-historique-achats-statut" +
                          (paye ? " profile-historique-achats-statut--paye" : " profile-historique-achats-statut--non-paye")
                        }
                      >
                        {paye ? TEXTS.historiqueAchatsPaye : TEXTS.historiqueAchatsNonPaye}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
